import { DesignData, ElementAddress, MediaText, Spread } from 'editor/src/store/design/types';

/** One laid-out frame: the text's graphemes ('\n' between typed lines) and its wrapped lines. */
export interface FrameLayout {
  graphemes: string[];
  lines: string[][];
  /** Height of each wrapped line, an empty one spaced (FabricPathText.getHeightOfLine). */
  lineHeights: number[];
  /** fabric's lineHeight factor: the last line counts as lineHeight / factor. */
  lineHeightFactor: number;
  /** A text taller than the frame by up to this much still fits (FabricPathText's overflow tolerance). */
  tolerance: number;
}

export interface FlowFrame {
  height: number;
  /** Lays the given text out in this frame, with the frame's own width, font and spacing. */
  layout: (text: string) => FrameLayout;
}

export interface FlowSlice {
  text: string;
  /** What the cut before this text took out ('', ' ', '\n', '\n\n'...); a frame without text has none. */
  joint?: string;
}

// between a frame's own text and the chain's text before it, when no cut recorded what joins them:
// text typed into an empty frame starts a paragraph
const DEFAULT_JOINT = '\n\n';

const SPACE_OR_TAB = /[ \t\r]/;

/**
 * Where each wrapped line starts and ends in the graphemes. A line after the first starts past the
 * '\n' of a typed line break, or past the one space a soft wrap drops, as in fabric's
 * Textbox._generateStyleMap, so cutting between two lines loses nothing.
 */
export function getLineRanges(graphemes: string[], lines: string[][]): Array<[number, number]> {
  const ranges: Array<[number, number]> = [];
  let position = 0;
  lines.forEach((line, i) => {
    if (i > 0 && (graphemes[position] === '\n' || SPACE_OR_TAB.test(graphemes[position] ?? ''))) {
      position += 1;
    }
    ranges.push([position, position + line.length]);
    position += line.length;
  });
  return ranges;
}

function getFittingLineCount(layout: FrameLayout, frameHeight: number) {
  let height = 0;
  for (let i = 0; i < layout.lines.length; i += 1) {
    const lineHeight = layout.lineHeights[i];
    if (height + lineHeight / layout.lineHeightFactor - frameHeight > layout.tolerance) {
      return i;
    }
    height += lineHeight;
  }
  return layout.lines.length;
}

const isBlank = (line: string[] | undefined) => !!line && !line.length;

/**
 * The line to cut before so that a paragraph (a run of non-empty lines, e.g. a verse) is not split
 * between two frames: its start, when the paragraph is not the first thing in the frame and fits in
 * the next frame whole, laid out at that frame's width. Otherwise the cut stays where the frame is full.
 */
function keepParagraphTogether(
  layout: FrameLayout,
  ranges: Array<[number, number]>,
  cut: number,
  nextFrame: FlowFrame,
) {
  const { lines } = layout;
  if (cut === 0 || cut >= lines.length || isBlank(lines[cut]) || isBlank(lines[cut - 1])) {
    return cut;
  }
  let start = cut - 1;
  while (start > 0 && !isBlank(lines[start - 1])) {
    start -= 1;
  }
  if (start === 0) {
    return cut;
  }
  let end = cut;
  while (end < lines.length - 1 && !isBlank(lines[end + 1])) {
    end += 1;
  }
  const nextLayout = nextFrame.layout(layout.graphemes.slice(ranges[start][0], ranges[end][1]).join(''));
  return getFittingLineCount(nextLayout, nextFrame.height) >= nextLayout.lines.length ? start : cut;
}

/**
 * Splits the text of a chain over its frames in order: each frame takes the lines that fit in it,
 * the last one takes the rest (and overflows when it is full). Empty lines at a cut are dropped
 * into the next slice's joint, so a frame never starts or ends with a gap, and a verse moves to the
 * next frame whole where it fits there.
 */
export function splitTextOverFrames(text: string, frames: FlowFrame[]): FlowSlice[] {
  const slices: FlowSlice[] = [];
  let remaining = text;
  let joint: string | undefined;
  frames.forEach((frame, index) => {
    if (!remaining) {
      slices.push({ text: '' });
      return;
    }
    if (index === frames.length - 1) {
      slices.push({ text: remaining, joint });
      remaining = '';
      return;
    }
    const layout = frame.layout(remaining);
    const ranges = getLineRanges(layout.graphemes, layout.lines);
    let cut = keepParagraphTogether(layout, ranges, getFittingLineCount(layout, frame.height), frames[index + 1]);
    if (cut >= layout.lines.length) {
      slices.push({ text: remaining, joint });
      remaining = '';
      return;
    }
    while (cut > 0 && isBlank(layout.lines[cut - 1])) {
      cut -= 1;
    }
    let next = cut;
    while (next < layout.lines.length && isBlank(layout.lines[next])) {
      next += 1;
    }
    if (cut === 0) {
      // too small for a line: the frame stays empty and the next one takes the text, joint and all
      slices.push({ text: '' });
      return;
    }
    const end = ranges[cut - 1][1];
    const nextStart = next < layout.lines.length ? ranges[next][0] : layout.graphemes.length;
    slices.push({ text: layout.graphemes.slice(0, end).join(''), joint });
    if (nextStart < layout.graphemes.length) {
      joint = layout.graphemes.slice(end, nextStart).join('');
      remaining = layout.graphemes.slice(nextStart).join('');
    } else {
      // only blank lines after the cut: the next frame keeps them as its text, since a frame without
      // text keeps no joint, and a new paragraph just started would be lost
      joint = '';
      remaining = layout.graphemes.slice(end).join('');
    }
  });
  return slices;
}

export interface TextFlowMember {
  element: MediaText;
  address: ElementAddress;
}

/**
 * The chains of linked frames in the design, each in frame order. A chain lives on one spread; a
 * copied spread carries a copy of the chain under the same id, which is a chain of its own. A copied
 * frame shares its original's index and sorts after it (uuids only grow).
 */
export function getTextFlowChains(designData: DesignData | undefined): TextFlowMember[][] {
  const chains = new Map<string, TextFlowMember[]>();
  designData?.spreads.forEach((spread, spreadIndex) => addSpreadChains(chains, spread, spreadIndex));
  return sortChains(chains);
}

/** The chain the text is a frame of. Only its spread is read: a chain never leaves one. */
export function getTextFlowChainOf(designData: DesignData | undefined, spreadIndex: number, uuid: number) {
  const spread = designData?.spreads[spreadIndex];
  if (!spread) {
    return undefined;
  }
  const chains = new Map<string, TextFlowMember[]>();
  addSpreadChains(chains, spread, spreadIndex);
  return sortChains(chains).find((chain) => chain.some(({ element }) => element.uuid === uuid));
}

/**
 * The value of the buyer's field for a text: a chain of linked frames is one field, asked for on its
 * first frame, which holds the chain's whole text; a later frame has no field (undefined).
 */
export function getTextFlowFieldText(
  designData: DesignData | undefined,
  spreadIndex: number | undefined,
  element: MediaText,
): string | undefined {
  const chain = getFieldChain(designData, spreadIndex, element);
  if (!chain) {
    return element.extra.text;
  }
  return chain[0].element.uuid === element.uuid ? getTextFlowChainText(chain) : undefined;
}

/** Whether the text is a linked frame after the first of its chain, which has no field of its own. */
export function isLaterTextFlowFrame(
  designData: DesignData | undefined,
  spreadIndex: number | undefined,
  element: MediaText,
) {
  const chain = getFieldChain(designData, spreadIndex, element);
  return !!chain && chain[0].element.uuid !== element.uuid;
}

function getFieldChain(designData: DesignData | undefined, spreadIndex: number | undefined, element: MediaText) {
  // read first: the walk is for linked texts only, and the buyer's panel asks on every keystroke
  if (!element.extra?.textFlow || spreadIndex === undefined) {
    return undefined;
  }
  const chain = getTextFlowChainOf(designData, spreadIndex, element.uuid);
  return chain && chain.length > 1 ? chain : undefined;
}

function addSpreadChains(chains: Map<string, TextFlowMember[]>, spread: Spread, spreadIndex: number) {
  spread.pages.forEach((page, pageIndex) =>
    page.groups.media?.forEach((element, elementIndex) => {
      if (element.type === 'text' && element.extra.textFlow) {
        const key = `${spreadIndex}/${element.extra.textFlow.id}`;
        const chain = chains.get(key) ?? [];
        chain.push({ element, address: { spreadIndex, pageIndex, elementIndex } });
        chains.set(key, chain);
      }
    }),
  );
}

function sortChains(chains: Map<string, TextFlowMember[]>) {
  return [...chains.values()].map((chain) =>
    chain.sort(
      (a, b) =>
        (a.element.extra.textFlow?.index ?? 0) - (b.element.extra.textFlow?.index ?? 0) ||
        a.element.uuid - b.element.uuid,
    ),
  );
}

export interface FrameBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

// frames drawn by hand overlap by a hair where they are meant to sit side by side or stacked
const OVERLAP_TOLERANCE_MM = 1;

function splitAtGaps<T>(items: T[], start: (item: T) => number, size: (item: T) => number): T[][] {
  const groups: T[][] = [];
  let end = -Infinity;
  [...items]
    .sort((a, b) => start(a) - start(b))
    .forEach((item) => {
      if (start(item) >= end - OVERLAP_TOLERANCE_MM) {
        groups.push([]);
      }
      groups[groups.length - 1].push(item);
      end = Math.max(end, start(item) + size(item));
    });
  return groups;
}

/**
 * Frames in reading order (a recursive XY-cut): columns left to right, each read top to bottom,
 * and a frame spanning several columns, like a heading, before the columns under it. Columns are
 * cut first, so two columns of two frames each read down one column, then the other.
 */
export function sortInReadingOrder<T>(items: T[], box: (item: T) => FrameBox): T[] {
  if (items.length < 2) {
    return items;
  }
  const columns = splitAtGaps(
    items,
    (item) => box(item).x,
    (item) => box(item).width,
  );
  if (columns.length > 1) {
    return columns.flatMap((column) => sortInReadingOrder(column, box));
  }
  // only the top band comes off: what is under it may split into columns again
  const [top, ...below] = splitAtGaps(
    items,
    (item) => box(item).y,
    (item) => box(item).height,
  );
  if (below.length) {
    return [...sortInReadingOrder(top, box), ...sortInReadingOrder(below.flat(), box)];
  }
  return [...items].sort((a, b) => box(a).y - box(b).y || box(a).x - box(b).x);
}

/**
 * The whole text of a chain: the frames' texts joined back with what the cuts took out. Frames
 * without text add nothing, so emptying a frame does not leave its joints behind twice.
 */
export function getTextFlowChainText(chain: TextFlowMember[]): string {
  return chain.reduce((text, { element }) => {
    if (!element.extra.text) {
      return text;
    }
    return text ? text + (element.extra.textFlow?.joint ?? DEFAULT_JOINT) + element.extra.text : element.extra.text;
  }, '');
}

/**
 * The chain's text with `text` added at its end. Adding to the selected frame alone would put it in
 * the middle of the chain.
 */
export function appendToTextFlowChain(chain: TextFlowMember[], text: string, separator: string) {
  const current = getTextFlowChainText(chain).trimEnd();
  return setTextFlowChainText(chain, current ? `${current}${separator}${text}` : text);
}

/**
 * The updates that make `chainText` the chain's whole text: all of it in the first frame and the
 * others emptied, so the next flow pours it over them all.
 */
export function setTextFlowChainText(chain: TextFlowMember[], chainText: string) {
  return chain.map(({ element, address }, index) => ({
    address,
    update: {
      extra: {
        ...element.extra,
        text: index === 0 ? chainText : '',
        textFlow: element.extra.textFlow && { ...element.extra.textFlow, joint: undefined },
      },
    } as Partial<MediaText>,
  }));
}

/**
 * The update that keeps a removed frame's text in its chain: it goes to the start of the next frame
 * (or the end of the previous one when it was the last), and the next flow spreads it again. A
 * removed first frame hands its host field key on too, even without text.
 */
export function getTextFlowHandOver(
  chain: TextFlowMember[],
  removedUuid: number,
): { address: ElementAddress; update: Partial<MediaText> } | undefined {
  const index = chain.findIndex(({ element }) => element.uuid === removedUuid);
  const removed = chain[index]?.element;
  // the host fills a chain through its first frame's key (linkTextFramesOperation), which the next frame takes
  const fieldKey = index === 0 ? removed?.extra.fieldKey : undefined;
  if (!removed || (!removed.extra.text && !fieldKey) || chain.length < 2) {
    return undefined;
  }
  const removedJoint = index > 0 ? removed.extra.textFlow?.joint : undefined;
  const next = chain[index + 1];
  if (next) {
    const { text, textFlow } = next.element.extra;
    const handedText = removed.extra.text;
    return {
      address: next.address,
      update: {
        extra: {
          ...next.element.extra,
          ...(handedText
            ? {
                text: text ? handedText + (textFlow?.joint ?? DEFAULT_JOINT) + text : handedText,
                textFlow: textFlow && { ...textFlow, joint: removedJoint },
              }
            : {}),
          fieldKey: fieldKey ?? next.element.extra.fieldKey,
        },
      },
    };
  }
  const { element: previous, address } = chain[index - 1];
  const { text, textFlow } = previous.extra;
  return {
    address,
    update: {
      extra: {
        ...previous.extra,
        text: text ? text + (removedJoint ?? DEFAULT_JOINT) + removed.extra.text : removed.extra.text,
        textFlow: text || !textFlow ? textFlow : { ...textFlow, joint: removedJoint },
      },
    },
  };
}
